import { useEffect, useState } from 'react';
import { dataHealthSettingsSchema, type AppSettings } from '@ace-tracker/shared';
import { Card, Field, SaveBar, TextInput } from './formParts';
import { useSaveSettings } from './useSaveSettings';

export function DataHealthSettingsForm({ settings }: { settings: AppSettings }) {
  const baseline = String(settings.dataHealth.staleAfterHours);
  const [staleAfterHours, setStaleAfterHours] = useState(baseline);
  const [validationError, setValidationError] = useState<string>();
  const { save, saving, error, savedAt, reset } = useSaveSettings('dataHealth');

  useEffect(() => setStaleAfterHours(baseline), [baseline]);

  const n = Number(staleAfterHours);
  const valid = staleAfterHours.trim() !== '' && Number.isInteger(n) && n >= 1 && n <= 720;

  function handleSave() {
    const parsed = dataHealthSettingsSchema.safeParse({ staleAfterHours: staleAfterHours.trim() === '' ? NaN : n });
    if (!parsed.success) {
      setValidationError('Stale after: enter a whole number of hours, 1–720');
      return;
    }
    save(parsed.data);
  }

  return (
    <div className="space-y-5">
      <Card
        title="Developer collector health"
        description="How the Data health page flags a developer's machine. This only changes how developers are classified — telemetry is untouched."
      >
        <div className="max-w-xs">
          <Field
            label="Stale after (hours)"
            hint="A developer whose collector has not run for this long is shown as Stale. The collector runs every 6 hours by default, so 24 allows for a laptop that was off overnight. 1–720."
            htmlFor="health-stale-after"
          >
            <TextInput
              id="health-stale-after"
              inputMode="numeric"
              className="max-w-[8rem] text-right tabular-nums"
              value={staleAfterHours}
              invalid={!valid}
              onChange={(e) => {
                setValidationError(undefined);
                setStaleAfterHours(e.target.value);
              }}
            />
          </Field>
        </div>
      </Card>

      <SaveBar
        dirty={staleAfterHours !== baseline}
        saving={saving}
        error={validationError ?? error}
        savedAt={savedAt}
        audit={settings.audit.dataHealth}
        onSave={handleSave}
        onDiscard={() => {
          setStaleAfterHours(baseline);
          setValidationError(undefined);
          reset();
        }}
      />
    </div>
  );
}
